import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import {
  isDbI18nEnabled,
  loadRemoteLocaleBundle,
  prefetchRemoteLocaleBundles,
} from './dbBundleLoader';
import en from './locales/en.json';
import es from './locales/es.json';
import pt from './locales/pt.json';
import ru from './locales/ru.json';

const STORAGE_KEY = 'ggj_language';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'pt', label: 'Português' },
  { code: 'ru', label: 'Русский' },
  { code: 'es', label: 'Español' },
] as const;

export type SupportedLanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];

function getStoredLanguage(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.some((l) => l.code === stored)) return stored;
  } catch {
    // ignore
  }
  return 'en';
}

export function setStoredLanguage(code: SupportedLanguageCode): void {
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // ignore
  }
}

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    pt: { translation: pt },
    ru: { translation: ru },
    es: { translation: es },
  },
  lng: getStoredLanguage(),
  fallbackLng: 'en',
  supportedLngs: ['en', 'pt', 'ru', 'es'],
  interpolation: {
    escapeValue: false,
  },
});

// Locales whose DB bundle has already been merged. Guards against an infinite
// loop: applyBundleToI18n() calls changeLanguage() to surface new keys, which
// re-emits 'languageChanged', which would otherwise re-trigger hydration →
// re-render storm. We only need to hydrate each locale once.
const hydratedLocales = new Set<string>();

async function hydrateLocaleFromDb(locale: string): Promise<void> {
  if (!isDbI18nEnabled()) return;
  const normalized = locale.split('-')[0]?.toLowerCase() || 'en';
  if (hydratedLocales.has(normalized)) return;
  hydratedLocales.add(normalized);
  const result = await loadRemoteLocaleBundle(normalized, i18n);
  if (!result.applied) {
    // Nothing applied (e.g. transient failure) — allow a later retry.
    hydratedLocales.delete(normalized);
    return;
  }
  if (import.meta.env.DEV) {
    console.info(`[i18n] DB bundle applied for "${normalized}" (${result.source}, ${result.keyCount} keys)`);
  }
}

void hydrateLocaleFromDb(i18n.language);

if (typeof window !== 'undefined' && isDbI18nEnabled()) {
  const prefetch = () => {
    void prefetchRemoteLocaleBundles(
      SUPPORTED_LANGUAGES.map((l) => l.code),
      i18n,
    );
  };
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => prefetch());
  } else {
    window.setTimeout(prefetch, 1500);
  }
}

i18n.on('languageChanged', (lng) => {
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = lng;
  }
  setStoredLanguage(lng as SupportedLanguageCode);
  void hydrateLocaleFromDb(lng);
});

// Set initial html lang
if (typeof document !== 'undefined' && document.documentElement) {
  document.documentElement.lang = i18n.language;
}

export { invalidateDbI18nCache } from './dbBundleLoader';
export { flatToNested, flattenLocaleJson } from './flatUtils';
export default i18n;
